/**
 * Wallet capability detection — EIP-7702 + EIP-5792 + delegation
 * signing path.
 *
 * Used by the Deposit Protection dialog (rendered from the profile
 * dropdown) to gate the enable flow on a wallet that can both
 *
 *   (a) sign an EIP-7710 delegation (the EIP-712 payload our
 *       `DepositProtectionEnforcer` consumes), and
 *   (b) execute redemptions either as a smart account or via an
 *       EIP-7702 authorization.
 *
 * (a) and (b) used to track together — any wallet that shipped 7702
 * also accepted `eth_signTypedData_v4` for delegation typed-data. That
 * stopped being true in mid-2026 when MetaMask landed a validator in
 * `@metamask/core` (`signature-controller/utils/validation.ts ::
 * validateDelegation`) that throws
 *   "External signature requests cannot sign delegations for internal
 *    accounts."
 * for any external-origin `eth_signTypedData_v4` call carrying a
 * delegation payload whose `delegator` matches a MetaMask-managed
 * account. That covers every MM EOA (and every MM Smart Account),
 * since MM treats all of them as "internal". The intended path for
 * MetaMask is now ERC-7715 (`wallet_requestExecutionPermissions`,
 * renamed mid-2025 from the original draft name
 * `wallet_grantPermissions` — MM ships both legacy and canonical
 * names across Flask versions; see
 * `hooks/use-grant-protection-permissions.ts`), which produces a
 * signed ERC-7710 delegation under the hood — same onchain
 * primitive, different signing UX.
 *
 * We therefore expose a `signingPath` on the result so the dialog and
 * the enable-mutation can route to the right signer:
 *
 *   - `'7710-direct'`  — `eth_signTypedData_v4` with the canonical
 *                        MDF Delegation typed-data. Used for Coinbase
 *                        Wallet, Rabby, Frame, and any future wallet
 *                        that doesn't gate raw delegation signing.
 *
 *   - `'7715-grant'`   — ERC-7715 RPC
 *                        (`wallet_requestExecutionPermissions`, with
 *                        legacy fallback to `wallet_grantPermissions`).
 *                        The wallet returns `permissionsContext` plus a
 *                        `delegationManager` address; we adapt that
 *                        into the same `protection_delegations` row
 *                        shape and the keeper redeems it via the
 *                        usual `redeemDelegation` flow. Currently
 *                        only MetaMask Smart Account in mainstream
 *                        ecosystem (Coinbase Smart Wallet ships 7715
 *                        too, but against its own SpendPermissions
 *                        manager — out of scope for this path).
 *
 * Detection strategy (most reliable -> most lenient):
 *
 *   1. Privy embedded wallet flag — embedded signer doesn't expose
 *      7702 today (mid-2026). Hard `false`.
 *
 *   2. MetaMask short-circuit — supports7702: true, signingPath:
 *      '7715-grant'. Bypasses the capability probe so a slow MM
 *      provider can't delay the dialog render.
 *
 *   3. EIP-5792 `wallet_getCapabilities` — wallets that adopt the
 *      atomic-batch standard publish a per-chain capability map. An
 *      `atomic.status === 'supported' | 'ready'` (or `atomicBatch`)
 *      capability is a good proxy for "this wallet can sign + send
 *      EIP-7702 transactions for this user".
 *
 *   4. Connector / wallet-name allowlist — for wallets that haven't
 *      shipped `wallet_getCapabilities` yet. Intentionally narrow — a
 *      false positive surfaces as a UX failure at signing time, not a
 *      security failure (the contract caveats remain the authoritative
 *      gate).
 */

import type { Address, Hex } from 'viem';

export type WalletConnectorId = string | undefined | null;

/**
 * Which JSON-RPC route the dialog should use to obtain a signed
 * delegation from this wallet. Both paths converge into the same
 * `protection_delegations` row shape and the same keeper redemption
 * pipeline; only the wallet-side UX differs.
 */
export type DelegationSigningPath = '7710-direct' | '7715-grant';

export interface WalletCapabilityResult {
  /** Whether the wallet can sign + redeem EIP-7710 delegations
   *  via EIP-7702 (or already runs as a smart account natively). */
  supports7702: boolean;
  /** Which signing JSON-RPC the dialog should call. Only meaningful
   *  when `supports7702 === true`. */
  signingPath: DelegationSigningPath;
  /** Human-readable wallet name for UX copy. */
  walletName: string;
  /** Marketing / docs link for the user, if any. Tooltips on the
   *  unsupported state link out to compatible wallets via this. */
  learnMoreUrl?: string;
  /** Reason code; useful for observability. */
  reason:
    | 'capability-confirmed'
    | 'connector-allowlisted'
    | 'embedded-unsupported'
    | 'metamask-via-7715'
    | 'unknown-wallet'
    | 'capability-missing';
}

interface CapabilityMap {
  /** EIP-5792 v2 shape. */
  atomic?: { status: 'supported' | 'ready' | 'unsupported' | string };
  /** EIP-5792 v1 shape (still emitted by some wallets). */
  atomicBatch?: { supported: boolean };
  /** Wallets that ship 7702 directly sometimes expose this key. */
  auth?: { supported: boolean };
  [k: string]: unknown;
}

/**
 * Wallets that ship EIP-7702 authorization support AND accept external
 * `eth_signTypedData_v4` calls for delegation payloads. MetaMask is
 * NOT in this list — see `METAMASK_CONNECTOR_IDS` below. MM has its
 * own short-circuit that routes it to the 7715 signing path instead.
 *
 * Connector IDs are wagmi-canonical (`coinbaseWalletSDK`, `injected`
 * for browser-extension Rabby/Frame, etc.). The label fallback handles
 * `injected` dispatching.
 */
const ALLOWLISTED_WALLETS = [
  {
    connectorId: 'coinbaseWalletSDK',
    label: 'Coinbase Wallet',
    url: 'https://www.coinbase.com/wallet',
  },
  {
    connectorId: 'com.coinbase.wallet',
    label: 'Coinbase Wallet',
    url: 'https://www.coinbase.com/wallet',
  },
  { connectorId: 'rabby', label: 'Rabby', url: 'https://rabby.io/' },
  { connectorId: 'io.rabby', label: 'Rabby', url: 'https://rabby.io/' },
  { connectorId: 'frame', label: 'Frame', url: 'https://frame.sh/' },
] as const;

/**
 * MetaMask connector / label hints. MM gets its own short-circuit
 * that returns `signingPath: '7715-grant'` so the dialog calls
 * `wallet_grantPermissions` instead of `eth_signTypedData_v4`.
 */
const METAMASK_CONNECTOR_IDS = ['metaMaskSDK', 'metaMask', 'io.metamask'] as const;
const METAMASK_LABEL_PATTERN = /metamask/i;

const LABEL_PATTERNS: Array<{ pattern: RegExp; label: string; url: string }> = [
  // Order matters for `resolveWalletName` — MM first so MM-labelled
  // injected providers attribute correctly even though the MM branch
  // routes through a different signing path.
  { pattern: /metamask/i, label: 'MetaMask', url: 'https://metamask.io/' },
  {
    pattern: /coinbase/i,
    label: 'Coinbase Wallet',
    url: 'https://www.coinbase.com/wallet',
  },
  { pattern: /rabby/i, label: 'Rabby', url: 'https://rabby.io/' },
  { pattern: /frame/i, label: 'Frame', url: 'https://frame.sh/' },
];

/**
 * Public list used to render the "Compatible: …" strip in the
 * incompatible state. MetaMask is the only wallet whose ERC-7715 grant
 * our DelegationManager can redeem today (see `detectEip7702Support`);
 * other wallets either can't install a DM-compatible 7702 delegator or
 * use a separate permission system (Coinbase Spend Permissions).
 */
export const COMPATIBLE_WALLETS = [
  { name: 'MetaMask', url: 'https://metamask.io/' },
] as const;

interface ProviderShape {
  request?: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
  isMetaMask?: boolean;
  isCoinbaseWallet?: boolean;
  isRabby?: boolean;
  isFrame?: boolean;
  isPhantom?: boolean;
  /** Privy embedded wallets stamp this for self-id. */
  walletClientType?: string;
}

/**
 * Resolve a wallet's display label from a heterogenous bag of hints.
 * Wagmi sometimes hands us a connector ID; Privy hands us a label
 * string; the EIP-1193 provider may stamp `isXxx` flags. We try all
 * three.
 */
function resolveWalletName(
  connectorId: WalletConnectorId,
  label: string | undefined,
  provider: ProviderShape | undefined,
): string {
  if (label) {
    for (const { pattern, label: prettyLabel } of LABEL_PATTERNS) {
      if (pattern.test(label)) return prettyLabel;
    }
    return label;
  }
  if (connectorId) {
    const hit = ALLOWLISTED_WALLETS.find((w) => w.connectorId === connectorId);
    if (hit) return hit.label;
  }
  if (provider?.isPhantom) return 'Phantom';
  if (provider?.isMetaMask) return 'MetaMask';
  if (provider?.isCoinbaseWallet) return 'Coinbase Wallet';
  if (provider?.isRabby) return 'Rabby';
  if (provider?.isFrame) return 'Frame';
  if (provider?.walletClientType === 'privy') return 'Privy embedded wallet';
  return 'Unknown wallet';
}

function urlForLabel(name: string): string | undefined {
  return LABEL_PATTERNS.find((p) => p.label === name)?.url;
}

/**
 * Top-level capability sniffer. Combines all four detection paths.
 * Returns synchronously when only static hints are available, and
 * uses the (async) `wallet_getCapabilities` RPC when a provider is
 * passed in.
 *
 * Callers should treat the result as advisory:
 *   - `supports7702: true` plus the `signingPath` hint means "render
 *     the Enable button and route through this signer at click time".
 *   - `supports7702: false` means "render the incompatible body with
 *     the compatible-wallets strip".
 *
 * Either way, the contract caveats remain the authoritative gate at
 * redemption time. A false positive here produces a clean UX-level
 * error (the signer-side hook surfaces actionable copy), not a
 * security incident.
 */
export async function detectEip7702Support(args: {
  connectorId?: WalletConnectorId;
  walletLabel?: string;
  provider?: ProviderShape;
  account?: Address;
  chainId: number;
}): Promise<WalletCapabilityResult> {
  const walletName = resolveWalletName(args.connectorId, args.walletLabel, args.provider);

  // ── Privy embedded short-circuit ──────────────────────────────
  if (
    args.provider?.walletClientType === 'privy' ||
    walletName === 'Privy embedded wallet'
  ) {
    return {
      supports7702: false,
      signingPath: '7710-direct',
      walletName: 'Privy embedded wallet',
      learnMoreUrl: 'https://docs.privy.io/wallets/wallets/overview',
      reason: 'embedded-unsupported',
    };
  }

  // ── MetaMask — the only supported wallet today ───────────────
  // Land Protection redemption executes on the holder's own account via
  // our MMDF DelegationManager, so enabling requires an ERC-7715 grant
  // that resolves to an MMDF ERC-7710 delegation our keeper can redeem.
  // Only MetaMask's Advanced Permissions
  // (`wallet_requestExecutionPermissions`) produces one — it provisions a
  // MetaMask Smart Account (the EIP7702StatelessDeleGator our DM trusts)
  // and returns a redeemable permissionContext.
  const isMetaMask =
    (args.connectorId &&
      (METAMASK_CONNECTOR_IDS as readonly string[]).includes(args.connectorId)) ||
    args.provider?.isMetaMask === true ||
    METAMASK_LABEL_PATTERN.test(args.walletLabel ?? '');
  if (isMetaMask) {
    return {
      supports7702: true,
      signingPath: '7715-grant',
      walletName: 'MetaMask',
      learnMoreUrl: 'https://metamask.io/',
      reason: 'metamask-via-7715',
    };
  }

  // ── Everything else: not supported (yet) ─────────────────────
  // The 7710-direct rail is RETIRED. Redemption needs the holder account
  // 7702-delegated to a DM-compatible delegator, and external wallets
  // can't install one: Rabby blocks a dapp-supplied EIP-7702
  // authorization and Phantom silently strips it (sends a plain tx), so
  // no DM-compatible upgrade lands. Coinbase Smart Wallet / Base App
  // don't speak ERC-7715 at all — they use a separate "Spend
  // Permissions" system (`SpendPermissionManager`) our DelegationManager
  // cannot redeem. So MetaMask is the only wallet whose grant our keeper
  // can redeem today.
  //
  // FUTURE WALLET SUPPORT — two distinct branches slot in HERE:
  //   1. A wallet that ships ERC-7715 returning an MMDF-redeemable
  //      ERC-7710 delegation → add a short-circuit returning
  //      `signingPath: '7715-grant'` (the existing redeem path covers it).
  //   2. Base App / Coinbase Smart Wallet → a SEPARATE Coinbase Spend
  //      Permissions integration (new signing path + a keeper rail that
  //      calls `SpendPermissionManager.spend`); it is NOT this 7715 path.
  return {
    // `signingPath` is a don't-care for an unsupported result (the wallet
    // can't enable); keep the historical '7710-direct' default.
    supports7702: false,
    signingPath: '7710-direct',
    walletName,
    learnMoreUrl: urlForLabel(walletName),
    reason: 'unknown-wallet',
  };
}
